/**
 * AlarmService — schedule/cancel local notifications per la sveglia.
 *
 * Strategia:
 *  - Su iOS la notifica usa il file .caf custom bundleato (max 30s, gestito OS).
 *    I file sono in `ios/App/App/*.caf` (registrati in pbxproj come bundle
 *    resources, vedi scripts/register-ios-sounds.py).
 *  - Quando l'utente tocca la notifica → l'app si apre e parte un loop ambient
 *    locale via @capacitor-community/native-audio (gestito altrove).
 *  - Web/preview: no-op silenzioso. La feature è destinata all'app installata.
 *
 * ID notifica fisso (1001) → schedulazione singola sostituibile: lo
 * schedule() di Capacitor sovrascrive automaticamente le notifiche con lo
 * stesso id (vedi docs LocalNotifications), così evitiamo race su edit ripetuti.
 */
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const ALARM_NOTIFICATION_ID = 1001;
const EXTRA_KEY = "smartdream_alarm";

// Mapping id ambient → filename .caf bundleato nel root iOS bundle.
// Deve corrispondere a /app/frontend/ios/App/App/<file>.caf
const SOUND_ID_TO_CAF = {
  rain: "pioggia.caf",
  ocean: "onde.caf",
  forest: "foresta.caf",
  fireplace: "camino.caf",
  whitenoise: "rumore-bianco.caf",
};

function isNative() {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

/**
 * Restituisce true se la permission è concessa. Su web ritorna false
 * silenziosamente (non triggera prompt).
 */
export async function ensureNotificationPermission() {
  if (!isNative()) return false;
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status.display === "granted") return true;
    if (status.display === "denied") return false;
    const req = await LocalNotifications.requestPermissions();
    return req.display === "granted";
  } catch {
    return false;
  }
}

/**
 * Calcola la prossima occorrenza locale di hh:mm a partire da `from`.
 * Se l'orario è già passato oggi → domani.
 */
function nextOccurrence(hour, minute, from = new Date()) {
  const target = new Date(from);
  target.setSeconds(0, 0);
  target.setHours(hour, minute, 0, 0);
  if (target.getTime() <= from.getTime()) {
    target.setDate(target.getDate() + 1);
  }
  return target;
}

/**
 * Pianifica la sveglia all'orario hh:mm.
 *  - alarmSoundId: identifica il suono ambient da loop-are dopo il tap notifica
 *    (es. "rain", "ocean", ...). Viene passato in `extra` e usato dal listener
 *    `localNotificationActionPerformed`.
 * Ritorna { scheduled: true, at: Date } oppure { scheduled: false, reason }.
 */
export async function scheduleAlarm({ hour, minute, alarmSoundId, title, body }) {
  if (!isNative()) {
    return { scheduled: false, reason: "not_native" };
  }
  const ok = await ensureNotificationPermission();
  if (!ok) {
    return { scheduled: false, reason: "no_permission" };
  }
  const at = nextOccurrence(hour, minute);
  // File .caf bundleato corrispondente al suono scelto. Su iOS questo nome
  // viene risolto come resource del bundle root (vedi pbxproj). Su Android
  // verrà mappato al raw resource omonimo (configurabile, opzionale).
  const soundFile = SOUND_ID_TO_CAF[alarmSoundId] || "pioggia.caf";
  try {
    await LocalNotifications.schedule({
      notifications: [
        {
          id: ALARM_NOTIFICATION_ID,
          title: title || "Sveglia SmartDream",
          body: body || "È ora di svegliarti",
          schedule: {
            at,
            allowWhileIdle: true,
          },
          sound: soundFile,
          // Metadata letti dal listener quando l'utente tocca la notifica
          extra: {
            [EXTRA_KEY]: true,
            alarmSoundId: alarmSoundId || null,
            scheduledFor: at.toISOString(),
          },
        },
      ],
    });
    return { scheduled: true, at };
  } catch (e) {
    return { scheduled: false, reason: "schedule_error", error: String(e) };
  }
}

export async function cancelAlarm() {
  if (!isNative()) return;
  try {
    await LocalNotifications.cancel({
      notifications: [{ id: ALARM_NOTIFICATION_ID }],
    });
  } catch {
    /* swallow */
  }
}

/**
 * Restituisce la notifica pendente per la sveglia (se esiste) oppure null.
 * Utile per UI che vuole mostrare "Sveglia attiva alle 07:00".
 */
export async function getPendingAlarm() {
  if (!isNative()) return null;
  try {
    const list = await LocalNotifications.getPending();
    const found = (list.notifications || []).find(
      (n) => n.id === ALARM_NOTIFICATION_ID
    );
    return found || null;
  } catch {
    return null;
  }
}

export const _internals = { ALARM_NOTIFICATION_ID, EXTRA_KEY };
